import dayjs from 'dayjs';
import { Handover, ReturnSourceType } from '@/types/common';
// The complete rung list; the one in @/types/common predates Dash Rent and has
// no WAITING_RETURN.
import { HandoverStatus } from '@/types/rental';

import type { HandoverLeg } from './HandoverDetailContent';

/**
 * The handover row as the detail endpoint actually returns it: the list-shaped
 * `Handover` plus the fields only `GET /v1/handovers/:id` carries.
 */
export interface HandoverDetailRecord extends Handover {
  returnReasonOther?: string;
  returnSourceType?: ReturnSourceType | string;
  returnSourceID?: string;
  returnSourceCode?: string;
  vehiclePlatNomor?: string;
}

/**
 * Which date the row still has pending, and therefore the only one ops can move.
 *
 * One predicate, because three places ask: the list's action menu, the detail
 * popup's link, and the modal that does the editing. When the tab-level gate and
 * the modal disagreed the modal rendered nothing — a menu item that did nothing.
 * Compared against the rent enum, the only one carrying WAITING_RETURN.
 */
export const pendingScheduleLeg = (status?: string): HandoverLeg | null => {
  if (status === HandoverStatus.WAITING_HANDOVER) return 'HANDOVER';
  if (status === HandoverStatus.WAITING_RETURN) return 'RETURN';
  return null;
};

/** Per-leg copy, so the three entry points cannot word the same action three ways. */
export const SCHEDULE_LEG_COPY: Record<
  HandoverLeg,
  { action: string; dateLabel: string; noun: string }
> = {
  HANDOVER: {
    action: 'Ubah Jadwal Serah Terima',
    dateLabel: 'Tanggal Serah Terima',
    noun: 'Serah terima',
  },
  RETURN: {
    action: 'Ubah Jadwal Pengembalian',
    dateLabel: 'Tanggal Pengembalian',
    noun: 'Pengembalian',
  },
};

export const displayValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

export const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY, HH:mm') : value;
};

/**
 * A planned date, with its window when there is one: "10/08/2026 • 09:00 - 11:00".
 *
 * The date is a plain `YYYY-MM-DD` calendar day at a hub, not an instant, so it
 * is rendered as given rather than converted — a timezone shift here would move
 * a mitra's appointment by a day. Times belong to the PICKUP slot only: a
 * scheduled return carries a date and nothing else, so pass no slot for it.
 */
export const formatSchedule = (
  value?: string | null,
  slot?: { startTime?: string | null; endTime?: string | null }
): string => {
  if (!value) return '-';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  const day = parsed.format('DD/MM/YYYY');
  const window = [slot?.startTime, slot?.endTime].filter(Boolean).join(' - ');
  return window ? `${day} • ${window}` : day;
};

export const formatNumber = (value?: number) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('id-ID').format(value);
};

export const formatOdometer = (value?: number) => {
  if (value === null || value === undefined) return '-';
  return `${formatNumber(value)} km`;
};

export const returnReasonLabels: Record<string, string> = {
  ACCIDENT: 'Accident',
  CONTRACT_END: 'Contract End',
  MITRA_RESIGN: 'Mitra Resign',
  CLIENT_REQUEST: 'Client Request',
  VEHICLE_SWAP: 'Vehicle Swap',
  DUTY_COMPLETED: 'Selesai Bertugas',
  OTHER: 'Other',
};

export const conditionLabels: Record<string, string> = {
  GOOD: 'Good',
  MINOR_DAMAGE: 'Minor Damage',
  MAJOR_DAMAGE: 'Major Damage',
};

/** Falls back to the vehicle-scoped plate the detail payload sometimes uses. */
export const resolveHandoverPlate = (
  handover?: HandoverDetailRecord | null
): string => handover?.platNomor || handover?.vehiclePlatNomor || '-';

/**
 * Handover equipment first, then anything the return leg recorded on top, so
 * the checklist covers both legs without listing an item twice.
 */
export const buildEquipmentChecklist = (
  handover?: HandoverDetailRecord | null
): string[] => {
  if (!handover) return [];

  const baseList =
    (handover.unitEquipment && handover.unitEquipment.length > 0
      ? handover.unitEquipment
      : handover.returnUnitEquipment) || [];

  const returnList = handover.returnUnitEquipment || [];
  const extras = returnList.filter((item) => !baseList.includes(item));

  return [...baseList, ...extras];
};

export const resolveReturnReason = (
  handover?: HandoverDetailRecord | null
): { value: string; detail: string } => ({
  value: handover?.returnReason
    ? returnReasonLabels[handover.returnReason] || handover.returnReason
    : '-',
  detail:
    handover?.returnReason === 'OTHER' && handover?.returnReasonOther
      ? ` - ${handover.returnReasonOther}`
      : '',
});

export const resolveCondition = (
  handover?: HandoverDetailRecord | null
): string =>
  handover?.returnCondition
    ? conditionLabels[handover.returnCondition] || handover.returnCondition
    : '-';
